import { type AutocompleteInputChangeReason } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useOutboundBenchOrdersQuery,
  type OrderStatus,
} from "../../../generated/graphql";
import { type BenchOrderOption } from "../bench-run";

/** The pause after the last keystroke before the picker searches. */
const SEARCH_DEBOUNCE_MS = 300;

const EMPTY_OPTIONS: BenchOrderOption[] = [];

/**
 * The order picker's server search over one partnership's orders. It waits
 * {@link SEARCH_DEBOUNCE_MS} after typing stops, and with no text it lists
 * the newest orders. Only typed text starts a search: MUI also rewrites the
 * input when an option is picked or cleared, and searching for that label
 * would hide every other order.
 */
const useBenchOrderSearch = ({
  partnershipUuid,
  orderStatuses,
}: {
  partnershipUuid: string | null;
  orderStatuses: readonly OrderStatus[];
}) => {
  const [typed, setTyped] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(typed.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [typed]);

  const input = useMemo(
    () => ({
      ediPartnershipUuid: partnershipUuid ?? "",
      search: search === "" ? null : search,
      orderStatuses: orderStatuses.length > 0 ? [...orderStatuses] : null,
    }),
    [orderStatuses, partnershipUuid, search],
  );

  // The internal app's watch queries default to `errorPolicy: "ignore"`,
  // which would show a failed search as "No orders match".
  const { data, loading, error } = useOutboundBenchOrdersQuery({
    errorPolicy: "none",
    skip: partnershipUuid === null,
    variables: { input },
  });

  const handleInputChange = useCallback(
    (_: unknown, value: string, reason: AutocompleteInputChangeReason) => {
      setTyped(reason === "input" ? value : "");
    },
    [],
  );

  return {
    results: data?.outboundBenchOrders ?? EMPTY_OPTIONS,
    loading,
    errorMessage: error?.message ?? null,
    handleInputChange,
  };
};

export default useBenchOrderSearch;
